/**
 * 用于特殊处理返回的 markdown 字符显示问题
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import {
  ensureMarkdownIntegrity,
  enableMarkdownLog,
  disableMarkdownLog,
  getLogConfig
} from './markdownIntegrityUtils';

interface P {
  text: string;
  showCursor: boolean;
  interrupt?: boolean;
  onComplete?: () => void;
}

// 打字效果的钩子函数
export const useTypewriterEffect = ({ text, showCursor, interrupt, onComplete }: P) => {
  const [displayText, setDisplayText] = useState('');
  const timerRef = useRef<number | null>(null);
  const prevTextRef = useRef('');
  const [fastMode, setFastMode] = useState(false);
  // 使用 ref 来追踪最新的 props 值
  const textRef = useRef(text);
  const showCursorRef = useRef(showCursor);
  const onCompleteRef = useRef(onComplete);
  const interruptRef = useRef(interrupt);
  
  // 更新 refs 以反映最新的 props
  useEffect(() => {
    textRef.current = text;
    showCursorRef.current = showCursor;
    onCompleteRef.current = onComplete;
    interruptRef.current = interrupt;
  }, [text, showCursor, onComplete, interrupt]);

  // 保存动画状态的引用
  const animationStateRef = useRef({
    isAnimating: false,
    targetText: '',
    currentIndex: 0,
    lastTimeStamp: 0
  });

  // 速度因子：控制打字效果的整体速度（值越大越慢）
  // 使用 useRef 而不是直接定义，避免造成重新渲染
  const speedFactorRef = useRef(6);

  // 暴露日志控制函数
  const toggleMarkdownLog = useCallback((enabled: boolean, level = 'info') => {
    if (enabled) {
      enableMarkdownLog(level as any);
    } else {
      disableMarkdownLog();
    }
    return getLogConfig();
  }, []);

  // 根据文本长度和速度因子计算显示参数
  const getDisplayParams = useCallback((remainingChars: number) => {
    // 使用 speedFactorRef.current 访问速度因子
    const speedFactor = speedFactorRef.current;

    // 如果开启快速模式，大幅提高输出速度
    if (fastMode) {
      return {
        frameInterval: 72,
        charsPerFrame: 1
      };
    }

    // 基于速度因子计算帧间隔（毫秒）
    let frameInterval;
    if (remainingChars > 50) {
      frameInterval = 8 * speedFactor;  // 长文本
    } else if (remainingChars > 20) {
      frameInterval = 10 * speedFactor; // 中等文本
    } else {
      frameInterval = 12 * speedFactor; // 短文本
    }

    // 基于速度因子计算每帧字符数
    // 速度因子越大，显示的字符越少
    let charsPerFrame;
    if (remainingChars > 50) {
      charsPerFrame = Math.max(1, Math.floor(5 / speedFactor)); // 长文本
    } else if (remainingChars > 20) {
      charsPerFrame = Math.max(1, Math.floor(3 / speedFactor)); // 中等文本
    } else {
      charsPerFrame = 1; // 短文本始终为1
    }

    return {
      frameInterval:72,
      charsPerFrame: 1
    };
  }, [fastMode]);

  // 创建一个更自然的打字效果（添加一些随机性）
  const getNaturalTypingDelay = useCallback((): number => {
    // 使用 speedFactorRef.current 访问速度因子
    const speedFactor = speedFactorRef.current;

    // 增加随机性出现的概率
    const shouldAddRandomDelay = Math.random() > 0.9;
    if (shouldAddRandomDelay) {
      // 增加随机延迟的时间，与速度因子成正比
      return Math.random() * 20 * speedFactor + 10;
    }
    return 0;
  }, []);

  // 取消动画
  const cancelAnimation = useCallback(() => {
    if (timerRef.current !== null) {
      cancelAnimationFrame(timerRef.current);
      timerRef.current = null;
    }
    animationStateRef.current.isAnimating = false;
  }, []);

  // 立即停止打字
  const stopImmediately = useCallback(() => {
    // 仅取消动画，保持当前文字
    cancelAnimation();

    // 调用完成回调，如果有的话
    if (onCompleteRef.current) {
      onCompleteRef.current();
    }
  }, [cancelAnimation]);

  // 动画帧处理函数
  const animationFrame = useCallback((timestamp: number) => {
    const state = animationStateRef.current;
    // 使用 ref 获取最新的 showCursor 值
    const currentShowCursor = showCursorRef.current;
    
    // 检查是否需要中断动画
    if (interruptRef.current) {
      stopImmediately();
      return;
    }
    
    // 计算时间差，控制输出速度
    const elapsed = timestamp - state.lastTimeStamp;
    const { frameInterval, charsPerFrame } = getDisplayParams(state.targetText.length - state.currentIndex);
    // console.log("🚀 ~ animationFrame ~ frameInterval:", frameInterval)
    // console.log("🚀 ~ animationFrame ~ charsPerFrame:", charsPerFrame)

    // 添加自然打字延迟
    const naturalDelay = getNaturalTypingDelay();
    const totalDelay = frameInterval + naturalDelay;

    // 如果时间间隔足够，添加新字符
    if (elapsed >= totalDelay) {
      // 更新时间戳，考虑实际延迟
      state.lastTimeStamp = timestamp;

      // 计算这一帧应该显示的字符数量
      const remainingChars = state.targetText.length - state.currentIndex;

      // 根据剩余字符数量动态调整每帧字符数
      let charsToShow = Math.min(remainingChars, charsPerFrame);

      // 检查Markdown语法完整性，可能需要额外显示字符
      const additionalChars = ensureMarkdownIntegrity(
        state.targetText,
        state.currentIndex,
        charsToShow
      );

      // 增加要显示的字符数，确保Markdown语法完整
      charsToShow += additionalChars;

      // 如果有剩余字符
      if (remainingChars > 0) {
        const newIndex = Math.min(state.currentIndex + charsToShow, state.targetText.length);
        const newText = state.targetText.substring(0, newIndex);
        setDisplayText(newText);
        state.currentIndex = newIndex;

        // 确保一直用 requestAnimationFrame 保持动画流畅
        timerRef.current = requestAnimationFrame(animationFrame);
      } else {
        // 动画完成
        cancelAnimation();
        // 更新引用文本
        prevTextRef.current = state.targetText;
        setFastMode(false);
        // 调用完成回调函数，使用 ref 中的值
        if (onCompleteRef.current && !currentShowCursor) {
          onCompleteRef.current();
        }
      }
    } else {
      // 时间间隔不够，继续等待
      timerRef.current = requestAnimationFrame(animationFrame);
    }
  }, [getDisplayParams, getNaturalTypingDelay, cancelAnimation, stopImmediately]);

  // 应用打字效果
  const applyTypewriterEffect = useCallback(() => {
    // 使用 ref 获取最新的值
    const currentText = textRef.current;
    const currentShowCursor = showCursorRef.current;
    const currentOnComplete = onCompleteRef.current;
    
    if (!currentText) {
      return;
    }

    // 如果光标不存在了，说明服务端全部返回了，需要尽快结束输出
    if (!currentShowCursor) {
      // 检查是否从打字模式切换到非打字模式
      const wasInTypingMode = animationStateRef.current.isAnimating;

      // 如果之前在打字模式，且有部分文本尚未显示，则以快速模式显示
      if (wasInTypingMode) {
        setFastMode(true);
      } else {
        // 历史记录之类的，直接展示全部
        setDisplayText(currentText);
        prevTextRef.current = currentText;
        
        // 文本已经直接设置完成，直接调用回调
        if (currentOnComplete && !currentShowCursor) {
          currentOnComplete();
        }
        return;
      }
    }

    // 检查是否需要中断
    if (interruptRef.current) {
      stopImmediately();
      return;
    }

    // 计算已有部分和新增部分
    const existingText = displayText || prevTextRef.current || '';
    let newText = '';

    // 确保不会出现负数索引
    if (currentText.length > existingText.length) {
      newText = currentText.substring(existingText.length);
    }

    // 如果没有新文本，不需要处理
    if (!newText.length) {
      prevTextRef.current = currentText;
      return;
    }

    // 设置新的动画状态 - 从当前显示的文本继续添加
    animationStateRef.current = {
      isAnimating: true,
      targetText: existingText + newText,
      currentIndex: existingText.length,
      lastTimeStamp: performance.now() // 使用当前时间作为起点，避免积累延迟
    };

    // 立即开始动画
    timerRef.current = requestAnimationFrame(animationFrame);

  }, [displayText, animationFrame, stopImmediately]);

  useEffect(() => {
    // 强制在每次 props 变化时应用打字效果
    applyTypewriterEffect();
  }, [text, showCursor, interrupt, applyTypewriterEffect]);

  // 组件卸载时清除动画
  useEffect(() => {
    return () => {
      cancelAnimation();
    };
  }, [cancelAnimation]);

  // 处理透明度渐变效果
  const getRenderedText = useCallback((showCursorParam: boolean) => {
    // 优先使用参数中的值，其次使用最新的 ref 值
    const cursorVisible = showCursorParam;
    
    // 如果没有文本，直接返回空字符串
    if (!displayText) return '';

    // 处理最后6个字符的透明度渐变效果
    if ((displayText.length >= 6 && cursorVisible) || fastMode) {
      const lastCharsToCheck = displayText.slice(-15); // 检查更大范围

      // 检查最后几个字符是否包含Markdown语法标记
      let containsMdSyntax = false;
      let mdPosition = -1;

      // 检查常见的Markdown语法
      const mdPatternsRegex = [
        /#+ /, /\*[^*]*$/, /\*\*[^*]*$/, /_[^_]*$/, /__[^_]*$/,
        /`[^`]*$/, /```[^`]*$/, /\[[^\]]*$/, /!\[[^\]]*$/,
        /> /, /- /, /\d+\. /
      ];

      for (const regex of mdPatternsRegex) {
        const match = lastCharsToCheck.match(regex);
        if (match) {
          containsMdSyntax = true;
          const pos = lastCharsToCheck.search(regex);
          mdPosition = (pos === -1) ? -1 : pos;
          break;
        }
      }

      // 如果包含Markdown语法且位置在可能影响渐变的范围内
      if (containsMdSyntax && mdPosition !== -1 && (lastCharsToCheck.length - mdPosition) <= 10) {
        // 不应用渐变效果，直接返回完整文本
        return displayText;
      }

      // 正常情况下应用渐变效果
      const mainText = displayText.slice(0, -6);
      const lastSixChars = displayText.slice(-6);

      // 当正在输入新文本时，使用原来的渐变效果（从高到低）
      const opacities = [0.8, 0.6, 0.4, 0.2, 0.1, 0.05];

      // 创建带有渐变效果的HTML
      const gradientHtml = lastSixChars
        .split('')
        .map((char, index) => `<span style="opacity: ${opacities[index]}; transition: opacity 0.2s;">${char}</span>`)
        .join('');

      return mainText + gradientHtml;
    }

    return displayText;
  }, [displayText, fastMode]);

  // 调整打字速度
  const setTypingSpeed = useCallback((speed: number) => {
    // 调整速度因子，范围限制在 0.5 到 5 之间
    speedFactorRef.current = Math.max(0.5, Math.min(5, speed));
    return speedFactorRef.current;
  }, []);

  return {
    displayText,
    getRenderedText,
    setTypingSpeed,
    toggleMarkdownLog
  };
};

export default useTypewriterEffect;
